import React from "react";
import { Box, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { GoogleLogoIcon, MicrosoftOutlookLogoIcon } from "@phosphor-icons/react";

// Sniper-link buttons: deep-link the user straight to the transactional email
// they're waiting on (the signup verification code or the password-reset
// link): a sender search in Gmail, the Junk folder in Outlook. `providers` comes
// from getInboxProviders(); the component renders nothing when it's empty
// (e.g. iCloud/Yahoo, where we have no reliable webmail deep-link, so we fall
// back to plain "check your inbox" copy). `onOpen(providerKey)` fires on click
// so each caller can attribute its own analytics event.

// The mark on each provider's button, keyed like getInboxProviders().
const INBOX_ICONS = {
  gmail: GoogleLogoIcon,
  outlookPersonal: MicrosoftOutlookLogoIcon,
  outlookBusiness: MicrosoftOutlookLogoIcon,
};

const inboxButtonSx = {
  position: "relative",
  overflow: "hidden",
  color: "#fff",
  textTransform: "none",
  fontSize: "1rem",
  fontWeight: 600,
  px: 3,
  py: 1.5,
  minWidth: { xs: "100%", sm: 210 },
  borderRadius: "12px",
  border: "1px solid rgba(218,165,32,0.55)",
  background:
    "linear-gradient(135deg, rgba(184,134,11,0.20) 0%, rgba(184,134,11,0.06) 100%)",
  boxShadow: "0 6px 22px rgba(218,165,32,0.20)",
  transition:
    "transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease, background 150ms ease",
  "& .MuiButton-startIcon": { mr: 1 },
  "& .MuiButton-startIcon svg": { width: 22, height: 22 },
  "&:hover": {
    borderColor: "#DAA520",
    background:
      "linear-gradient(135deg, rgba(184,134,11,0.30) 0%, rgba(184,134,11,0.12) 100%)",
    boxShadow: "0 10px 30px rgba(218,165,32,0.34)",
    transform: "translateY(-1px)",
  },
  // Periodic light sweep so the inbox CTA is the focal point (where the user
  // must go next to get their code / reset link).
  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-160%",
    width: "55%",
    height: "100%",
    background:
      "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)",
    transform: "skewX(-20deg)",
    animation: "inboxShine 3.2s ease-in-out infinite",
    pointerEvents: "none",
  },
  "@keyframes inboxShine": {
    "0%": { left: "-160%" },
    "55%": { left: "170%" },
    "100%": { left: "170%" },
  },
  "@media (prefers-reduced-motion: reduce)": {
    "&::after": { display: "none" },
  },
};

const InboxButtons = ({ providers, onOpen, sx }) => {
  const { t } = useTranslation();
  if (!providers || providers.length === 0) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5,
        justifyContent: "center",
        flexWrap: "wrap",
        mt: 3.5,
        ...sx,
      }}
    >
      {providers.map((p) => {
        const label = t(`verifyAccount.openInbox.${p.key}`);
        const Icon = INBOX_ICONS[p.key];
        return (
          <Button
            key={p.key}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<Icon weight="fill" aria-hidden />}
            onClick={() => onOpen?.(p.key)}
            aria-label={t("verifyAccount.openInbox.ariaOpensInNewTab", { label })}
            sx={inboxButtonSx}
          >
            {label}
          </Button>
        );
      })}
    </Box>
  );
};

export default InboxButtons;
